import { useCallback, useEffect, useState } from "react";
import { Languages, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { translateTrip, getTrip } from "@/lib/api";
import { useLang, LANG_LABEL } from "@/i18n";

export const TranslateBar = ({ trip, translations, setTranslations, showOriginal, setShowOriginal }) => {
  const { t, lang } = useLang();
  const [busy, setBusy] = useState(false);
  const source = trip.lang || "it";
  const has = !!translations[lang];

  const run = useCallback(async () => {
    setBusy(true);
    try {
      let res = await translateTrip(trip.id, lang);
      for (let i = 0; i < 60 && res.status === "generating"; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        const fresh = await getTrip(trip.id);
        res = { status: fresh.translation_status?.[lang] };
        if (res.status === "ready") setTranslations(fresh.translations || {});
      }
      if (res.status !== "ready") throw new Error();
      setTranslations((await getTrip(trip.id)).translations || {});
      setShowOriginal(false);
    } catch {
      toast.error(t("Traduzione non riuscita. Riprova."));
    } finally {
      setBusy(false);
    }
  }, [trip.id, lang, setTranslations, setShowOriginal, t]);

  useEffect(() => {
    if (source !== lang && !translations[lang] && trip.translation_status?.[lang] === "generating") run();
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  if (source === lang) return null;

  return (
    <div data-testid="translate-bar" className="no-print flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4">
      <p className="flex items-center gap-2 text-sm text-emerald-900"><Languages className="h-4 w-4" />{t("Questo itinerario è stato creato in {l}.", { l: t(LANG_LABEL[source]) })}</p>
      {has ? (
        <button data-testid="translate-toggle-button" onClick={() => setShowOriginal(!showOriginal)}
          className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-200 hover:bg-emerald-100">
          {showOriginal ? t("Mostra traduzione") : t("Mostra originale")}
        </button>
      ) : (
        <button data-testid="translate-trip-button" onClick={run} disabled={busy}
          className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-70">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Languages className="h-4 w-4" />}
          {busy ? t("Traduzione in corso...") : t("Traduci in {l}", { l: t(LANG_LABEL[lang]) })}
        </button>
      )}
    </div>
  );
};
